export const portfolio = [
  {
    title: "Mable",
    description:
      "Lead engineer on a team migrating multiple WordPress sites to a Next.js + Contentful headless stack",
    link: "mable",
    thumbnail: "mable.jpg",
    labels: [
      "Next.js",
      "TypeScript",
      "React",
      "Contentful",
      "Headless CMS",
      "WordPress",
      "Jest",
      "Playwright",
      "Storybook",
      "AWS",
      "GitHub Actions",
      "CI/CD",
    ],
    content: [
      {
        items: [
          {
            title: "Migrating multiple WordPress sites to Contentful",
            description: `
              <b>At Mable, I'm the website lead engineer, managing a small team of developers.</b>
              <p>Mable owns several websites connecting social workers to people with disabilities or older adults. All of them were running on WordPress — workable, but increasingly hard to maintain and scale as the business grew.</p>
              <p>My team led the migration of multiple WordPress sites to a headless CMS stack, with Contentful at the core. The biggest lift was the main Mable website: 1000+ pages, content spread across years of WordPress edits, and a migration that touched multiple teams and stakeholders across the business.</p>
              <p>I ran the evaluation process — research, proofs of concept with Storyblok and Contentful, and stakeholder sessions — before settling on Contentful as the best fit. We rebuilt the sites using Next.js, with a strong focus on making the Contentful components intuitive for producers who update the website daily. This included building custom tools to support their workflow — like a rich text editor extended with formatting options editors actually reach for.</p>
              <p>Alongside this, we built and maintain a shared component library managed with Storybook.</p>
              <p>The whole stack is hosted on AWS, with CI/CD pipelines through GitHub Actions covering testing (Jest and Playwright) and deployment</p>
              <p>The migration went smoothly and landed on time. The business noticed — it got a strong reception internally, with teams that had been frustrated by the old setup finally having something they could work with.</p>
            `,
            image: "mable1.png",
          },
          {
            title: "Sign up microservice, component library",
            description: `
              <b>On Mable, both social workers and clients can log in to use our application (built in Angular and managed by another team).</b>
              <p>To improve the signup flow, we split it into a standalone React microservice. Beyond signup, we also built several other small React apps for the platform — a disability quiz, and a tool to search and filter support workers pulled directly from our app's data.</p>
              <p>All of these apps use our shared component library. During the transition period between WordPress and the full Contentful migration, they were integrated into the WordPress sites as plugins — and built to slot straight into the new headless setup when the time came.</p>
              <p>They're deployed on AWS, with automated deployments through GitHub Actions keeping things predictable.</p>
            `,
            image: "mable2.png",
          },
          {
            title: "Clean and Maintain WordPress Websites",
            description: `
              <b>Before joining Mable, the websites were managed by multiple external contractors, resulting in disorganized codebases and bloated websites overloaded with unnecessary plugins.</b>
              <p>One of my first tasks was to streamline the WordPress environments and establish a scalable, maintainable structure. This involved cleaning up the themes, removing unused or redundant plugins, and consolidating functionality into custom widgets built using Elementor's custom features for reusability.</p>
              <p>Testing was introduced using Cypress to ensure stability and quality. Additionally, the WordPress theme content was placed under version control in GitHub, with a deployment pipeline set up through GitHub Actions. Less manual back-and-forth, fewer deployment surprises, and consistent environments across the team.</p>
            `,
            image: "mable3.png",
          },
        ],
      },
    ],
  },
  {
    title: "CESE (NSW Education Department)",
    link: "cese",
    description:
      "Rebuilt a data-heavy government Census tool for NSW schools using Vue.js and Vuetify",
    thumbnail: "cese.jpg",
    labels: [
      "Vue.js",
      "Vuetify",
      "GitHub",
      "Tailwind",
      "Charts",
      "Axios",
      "Vuex",
      "REST API",
    ],
    content: [
      {
        items: [
          {
            title: "Rebuilding a critical Census tool for NSW schools",
            description: `
              <b>Rebuilding a government data tool used by schools across New South Wales.</b>
              <p>CESE, a project for the NSW Department of Education, aimed to rebuild an outdated internal tool used by schools across New South Wales to submit Census data about their students. This data included metrics such as attendance rates, languages studied, and other essential academic statistics.</p>
              <p>The application consisted of two main parts: one interface for schools to enter and manage their data, and another for administrators to review submissions and generate detailed charts and reports. Built from the ground up using <b>Vue.js</b> (Vue 2) as a mandated framework, the project utilized a custom component library provided by the department, based on Vuetify, to ensure consistency in design and functionality.</p>
            `,
            image: "cese1.png",
          },
          {
            title: "Managing complexity with reusable components",
            description: `
              <p>The CESE tool needed to handle massive amounts of data efficiently, as most of the information was displayed in data tables, input forms, and visualized in interactive charts. The application connected to multiple API endpoints, making it a data-heavy and highly interconnected system.</p>
              <p>Given the complexity of the data structure, our approach prioritized reusability and modularity. Key components such as tables, forms, and charting modules were designed to be highly configurable and reusable across the application. Tailwind CSS was used alongside Vuetify to enhance the styling flexibility, while Vuex handled the state management, ensuring smooth data flow across the app.</p>
              <p>The result was an application schools and administrators could actually use without friction.</p>
            `,
            image: "cese2.png",
          },
        ],
      },
    ],
  },
  {
    title: "Wilde",
    link: "wilde",
    thumbnail: "wilde.jpg",
    description:
      "Solo developer on a Shopify Hydrogen e-commerce site with custom GSAP animations",
    labels: [
      "React",
      "TypeScript",
      "Remix",
      "Hydrogen",
      "Shopify",
      "GraphQL",
      "GSAP",
      "Sass",
    ],
    content: [
      {
        items: [
          {
            description: `
              <b>Building a vibrant e-commerce experience with Shopify Hydrogen</b>
              <p>Wilde is an e-commerce website for a brand specializing in protein chips, designed to deliver an engaging and fun shopping experience. The project leveraged Shopify's <b>Hydrogen</b>, a headless CMS optimized for Shopify stores, allowing me to connect seamlessly to the store’s backend while creating a highly interactive front-end experience.</p>
              <p>As the sole developer, I used <b>GSAP</b> (GreenSock Animation Platform) extensively to create smooth and dynamic animations throughout the website. These animations brought the site to life, enhancing user engagement with playful transitions and interactive elements. </p>
              <p>I also worked with basic <b>GraphQL</b> queries to fetch product data directly from Shopify, ensuring accurate and efficient data retrieval. One of the project’s highlights was the custom "Build Your Box" feature, which allowed users to mix and match products. Developing this feature required a deep dive into Shopify’s ecosystem and APIs to handle the unique cart and inventory logic seamlessly.</p>
              <p>With a blend of <b>React</b>, <b>Remix</b>, and the Shopify Hydrogen framework, the result was a fast, visually distinctive e-commerce site that matched Wilde’s brand identity.</p>
            `,
            video: "wilde.mp4",
          },
        ],
      },
    ],
  },
  {
    title: "Intelematics",
    link: "intelematics",
    description:
      "Angular and React apps for an RACV-affiliated automotive connected-device company",
    thumbnail: "intelematics.jpg",
    labels: [
      "React",
      "Angular",
      "TypeScript",
      "WordPress",
      "GitHub",
      "REST API",
      "Power BI",
      "Mapbox",
      "OpenStreetMap",
      "Sass",
    ],
    content: [
      {
        items: [
          {
            description: `
              <b>Comprehensive Development Across Automotive Platforms</b>
              <p>Intelematics, a company affiliated with RACV, builds automotive connected-device products. During my time there, I contributed to a wide range of projects spanning technologies such as <b>React</b>, <b>Angular</b>, and <b>WordPress</b>.</p>
              <p>One notable project was an Angular-based troubleshooting and incident management platform. This app allowed users to diagnose car issues and connect directly with support personnel. I worked on improving the interface, implementing complex workflows, and ensuring an intuitive user experience.</p>
              <p>Another significant contribution was the maintenance of Intelematics’ <b>WordPress</b> website. From building custom plugins to optimizing performance and SEO, I kept the site in good shape and aligned with the brand.</p>
            `,
            image: "intelematics1.png",
          },
          {
            description: `
              <b>Developing Traffic Insights and a Vehicle Marketplace</b>
              <p>One of my key projects was building a React-based traffic insights application from the ground up. This tool, powered by <b>Mapbox</b> and <b>OpenStreetMap</b>, provided detailed traffic data visualizations. Users could filter data dynamically to view traffic volume for specific areas and timeframes, with data pulled from <b>Power BI</b> via custom APIs. The application featured a clean, interactive map interface for exploring traffic patterns and volume.</p>
              <p>Additionally, my last project was a website similar to Carsales, designed specifically for RACV. This platform in Angular fetched vehicle data directly from RACV’s database and offered users robust filtering options to explore and search for cars.</p>
            `,
            image: "intelematics2.webp",
          },
        ],
      },
    ],
  },
  {
    title: "Virgin and British Paint",
    link: "web-apps",
    description:
      "Vue.js interactive web apps for Virgin Airlines and British Paints at DDB digital agency",
    thumbnail: "web-apps.jpg",
    labels: ["Vue.js", "GSAP", "Tailwind", "UX/UI", "Digital Agency"],
    content: [
      {
        items: [
          {
            description: `
              <b>Creative interactive work at DDB</b>
              <p>During my time at <b>DDB</b>, a digital agency, I worked on two web applications built with <b>Vue.js</b> for well-known consumer brands.</p>
              
              <p><b>British Paints - Find Your Perfect Color</b></p>
              <p>This interactive web app guided users on a personalized journey to find the perfect color for their painting projects. The experience culminated in an interactive palette where users could zoom in to explore recommended colors or browse others. Each color was linked to paint references available in stores, making it practical for users to act on their selections offline.</p>
              <p>The project emphasized intuitive design and fluid interactivity, ensuring a delightful user experience that aligned with British Paints' brand identity.</p>
              
              <p><b>Virgin Airlines - Accent Analyzer</b></p>
              <p>For Virgin Airlines, I built a fun and engaging app designed to analyze the user's accent and determine whether it leaned more towards an Aussie or a Kiwi. Through animated screens, users could record their voice and submit the audio to an external tool for analysis. The results provided a percentage breakdown of their Australian or New Zealand accent and redirected them to promotional offers based on their result.</p>
            `,
            image: "webapp.png",
          },
        ],
      },
    ],
  },
  {
    title: "Australia Post",
    link: "australia-post",
    thumbnail: "auspost.jpg",
    description:
      "Animated campaign microsite for Australia Post, built in vanilla JS with GSAP for AEM",
    labels: ["JavaScript", "jQuery", "HTML", "Sass", "GSAP", "AEM"],
    content: [
      {
        items: [
          {
            description: `
              <b>Building a Campaign Microsite for Australia Post</b>
              <p>As a contractor for <b>Australia Post</b>, I worked on an exciting campaign called <b>The Great Aussie Coin Hunt</b>. This campaign celebrated Australia's culture with a series of special coins, each representing iconic Australian themes.</p>
              <p>I developed the campaign microsite using <b>Vanilla JavaScript</b> and <b>jQuery</b>, adhering to the client's specific requirements. The microsite needed to be split into smaller modules to integrate seamlessly into their <b>Adobe Experience Manager (AEM)</b> platform. Despite these constraints, the project offered plenty of creative opportunities.</p>
              <p>The microsite was heavy on animations, which were built using <b>GSAP</b> (GreenSock Animation Platform) to create smooth and engaging transitions. It was a joy to work on, blending technical challenges with creative problem-solving to deliver a fun, interactive experience that matched the campaign's energy.</p>
            `,
            video: "auspost.mp4",
          },
        ],
      },
    ],
  },
  {
    title: "Others",
    link: "others",
    description:
      "A decade of work across Blackmagic Design, Nintex, and personal projects",
    thumbnail: "others.jpg",
    labels: [
      "React",
      "TypeScript",
      "Redux",
      "Angular",
      "AngularJS",
      "Sass",
      "WordPress",
      "WooCommerce",
    ],
    content: [
      {
        items: [
          {
            description: `
                <b>Blackmagic Design - Prototyping Video Editing Interfaces</b>
                <p>While at <b>Blackmagic Design</b>, I contributed to several prototypes aimed at testing new interfaces for camera software and DaVinci Resolve, their industry-leading video editing software. These projects were essential in exploring new workflows and UI patterns for professionals in the film industry. I used <b>AngularJS</b> and began diving into <b>React</b> with <b>Redux</b> to build interactive and performant solutions.</p>
              `,
            image: "bmd.jpg",
          },
          {
            description: `
            <b>Nintex - Analytics Dashboard</b>
            <p>During my time contracting for <b>Nintex</b>, I worked on an analytics tool that visualized user activity across their suite of apps. The project required building charts and data-driven components with multiple filtering options, helping users make sense of their usage data and gain actionable insights.</p>
          `,
            image: "nintex.webp",
          },
          {
            description: `
            <b>Personal Projects</b>
            <p>Some of my personal projects were done for friends. One involved building a <b>Vue.js</b> website and contributing to some features for their app, which focused on feedback sharing between employers and employees. <br/><br/>Another was creating a <b>WordPress/WooCommerce</b> website to sell tarot cards. These projects allowed me to tackle diverse challenges, from interactive web app development to e-commerce functionality.</p>`,
            image: "gestaltra.png",
          },
          {
            description: `
            <b>Early Career - UX and Design</b>
            <p>Before transitioning fully into development, I spent time in UX/UI. I worked on designing and building landing pages, emails, animated banners, and web designs. <br/><br/>Though I’ve since shifted away from design work, this experience gave me a keen eye for detail, which helps me communicate with designers and maintain design integrity in projects.</p>
          `,
            image: "melbourneuni.webp",
          },
        ],
      },
    ],
  },
];
